"use client";

import {Slot, Slottable} from "@radix-ui/react-slot";
import {cva, type VariantProps} from "class-variance-authority";
import {LoaderCircle} from "lucide-react";
import * as React from "react";

import {ComponentProps} from "react";
import {cn} from "../utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-90",
        secondary: "bg-accent text-secondary-foreground hover:opacity-90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-border text-card-foreground hover:bg-secondary hover:border-border hover:text-foreground",
        ghost: "hover:bg-accent text-muted-foreground hover:text-foreground",
        link: "text-card-foreground underline-offset-4 hover:underline hover:text-foreground",
      },
      size: {
        default: "h-8 rounded-sm px-4 py-2",
        md: "h-7 rounded-sm px-3 py-2 text-xs",
        sm: "h-6 rounded-sm px-3 text-xs",
        lg: "h-10 rounded-sm px-8",
        icon: "h-6 w-6 rounded-sm",
      },
    },
    defaultVariants: {variant: "default", size: "default"},
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

function Button({
  className,
  loading = false,
  children,
  disabled,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({variant, size, className}))}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <>
          <LoaderCircle
            className="mr-2 animate-spin"
            size={12}
            strokeWidth={2}
            aria-hidden="true"
          />
          <Slottable>{children}</Slottable>
        </>
      ) : (
        <Slottable>{children}</Slottable>
      )}
    </Comp>
  );
}

export {Button, buttonVariants};
